import * as THREE from 'three';
import { Annotation } from '../annotation.js';
import { CameraMode } from '../../shared/defines.js';
import { View } from '../../viewer-utils/view.js';
import { Utils } from '../../shared/utils.js';
import { EventDispatcher } from '../../viewer-utils/event-dispatcher.js';
import { PointCloudOctree } from '../point-cloud-octree.js';
import { Measure } from '../tools/measure.js';
import { Profile } from '../tools/profile.js';
import { Volume, BoxVolume } from '../../viewer-utils/tools/volume.js';
import { PolygonClipVolume } from '../../viewer-utils/tools/polygon-clip-volume.js';

export class Scene extends EventDispatcher {
  annotations: Annotation;
  scene: THREE.Scene;
  sceneBG: THREE.Scene;
  scenePointCloud: THREE.Scene;
  cameraP: THREE.PerspectiveCamera;
  cameraO: THREE.OrthographicCamera;
  cameraBG: THREE.Camera;
  cameraScreenSpace: THREE.OrthographicCamera;
  cameraMode: CameraMode;
  overrideCamera: THREE.Camera | null;
  pointclouds: PointCloudOctree[];
  _measurements: Measure[];
  _profiles: Profile[];
  _volumes: Volume[];
  polygonClipVolumes: PolygonClipVolume[];
  geopackages: unknown[];
  fpControls: unknown;
  orbitControls: unknown;
  inputHandler: unknown;
  view: View;
  directionalLight: THREE.DirectionalLight | null;
  referenceFrame!: THREE.Object3D;
  fov: number = 60;

  constructor() {
    super();

    this.annotations = new Annotation();

    this.scene = new THREE.Scene();
    this.sceneBG = new THREE.Scene();
    this.scenePointCloud = new THREE.Scene();

    this.cameraP = new THREE.PerspectiveCamera(this.fov, 1, 0.1, 1000 * 1000);
    this.cameraO = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000 * 1000);
    this.cameraBG = new THREE.Camera();
    this.cameraScreenSpace = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    this.cameraMode = CameraMode.PERSPECTIVE;
    this.overrideCamera = null;
    this.pointclouds = [];

    this._measurements = [];
    this._profiles = [];
    this._volumes = [];
    this.polygonClipVolumes = [];
    this.geopackages = [];

    this.fpControls = null;
    this.orbitControls = null;
    this.inputHandler = null;

    this.view = new View();

    this.directionalLight = null;

    this.initialize();
  }

  /** Returns a shallow copy of measurements. Use addMeasurement/removeMeasurement to mutate. */
  get measurements(): Measure[] {
    return this._measurements.slice();
  }

  /** Returns a shallow copy of profiles. Use addProfile/removeProfile to mutate. */
  get profiles(): Profile[] {
    return this._profiles.slice();
  }

  /** Returns a shallow copy of volumes. Use addVolume/removeVolume to mutate. */
  get volumes(): Volume[] {
    return this._volumes.slice();
  }

  /**
   * NOTE: dead code path in practice - `pointcloud.root` is a raw geometry node until nodes
   * are streamed into tree nodes, so it never carries its own `.geometryNode`; this mirrors
   * the pre-existing (always-skipping) runtime behavior while remaining type-safe.
   */
  estimateHeightAt(position: THREE.Vector3): number | null {
    interface HeightTraversalNode {
      getBoundingBox(): THREE.Box3;
      geometryNode?: {
        mean?: THREE.Vector3;
        boundingBox: THREE.Box3;
        spacing?: number;
      };
      children?: HeightTraversalNode[];
    }

    let height: number | null = null;
    let fromSpacing = Infinity;

    for (let pointcloud of this.pointclouds) {
      const root = pointcloud.root as unknown as HeightTraversalNode | null;
      if (!root || root.geometryNode === undefined) {
        continue;
      }

      let pHeight: number | null = null;
      let pFromSpacing = Infinity;

      let lpos = position.clone().sub(pointcloud.position);
      lpos.z = 0;
      let ray = new THREE.Ray(lpos, new THREE.Vector3(0, 0, 1));

      let stack: HeightTraversalNode[] = [root];
      while (stack.length > 0) {
        let node = stack.pop()!;
        let box = node.getBoundingBox();

        let inside = ray.intersectBox(box, new THREE.Vector3());

        if (!inside) {
          continue;
        }

        const geometryNode = node.geometryNode;
        if (!geometryNode) {
          continue;
        }
        let h =
          (geometryNode.mean?.z ?? 0) + pointcloud.position.z + geometryNode.boundingBox.min.z;

        if ((geometryNode.spacing ?? Infinity) <= pFromSpacing) {
          pHeight = h;
          pFromSpacing = geometryNode.spacing ?? Infinity;
        }

        for (let child of node.children ?? []) {
          if (child?.geometryNode) {
            stack.push(child);
          }
        }
      }

      if (height === null || pFromSpacing < fromSpacing) {
        height = pHeight;
        fromSpacing = pFromSpacing;
      }
    }

    return height;
  }

  getBoundingBox(pointclouds = this.pointclouds): THREE.Box3 {
    let box = new THREE.Box3();

    this.scenePointCloud.updateMatrixWorld(true);
    this.referenceFrame.updateMatrixWorld(true);

    for (let pointcloud of pointclouds) {
      pointcloud.updateMatrixWorld(true);

      let pointcloudBox = pointcloud.pcoGeometry.tightBoundingBox
        ? pointcloud.pcoGeometry.tightBoundingBox
        : pointcloud.boundingBox;
      let boxWorld = Utils.computeTransformedBoundingBox(pointcloudBox, pointcloud.matrixWorld);
      box.union(boxWorld);
    }

    return box;
  }

  addPointCloud(pointcloud: PointCloudOctree): void {
    this.pointclouds.push(pointcloud);
    this.scenePointCloud.add(pointcloud);

    this.dispatchEvent({
      type: 'pointcloud_added',
      pointcloud: pointcloud,
    });
  }

  removePointCloud(pointcloud: PointCloudOctree): void {
    const index = this.pointclouds.indexOf(pointcloud);
    if (index > -1) {
      this.pointclouds.splice(index, 1);
      this.scenePointCloud.remove(pointcloud);

      const disposable = pointcloud as unknown as { dispose?: () => void };
      if (disposable.dispose) {
        disposable.dispose();
      }

      this.dispatchEvent({
        type: 'pointcloud_removed',
        pointcloud: pointcloud,
      });
    }
  }

  addVolume(volume: Volume): void {
    this._volumes.push(volume);
    this.dispatchEvent({
      type: 'volume_added',
      scene: this,
      volume: volume,
    });
  }

  removeVolume(volume: Volume): void {
    let index = this._volumes.indexOf(volume);
    if (index > -1) {
      this._volumes.splice(index, 1);

      this.dispatchEvent({
        type: 'volume_removed',
        scene: this,
        volume: volume,
      });
    }
  }

  addPolygonClipVolume(volume: PolygonClipVolume): void {
    this.polygonClipVolumes.push(volume);
    this.dispatchEvent({
      type: 'polygon_clip_volume_added',
      scene: this,
      volume: volume,
    });
  }

  removePolygonClipVolume(volume: PolygonClipVolume): void {
    let index = this.polygonClipVolumes.indexOf(volume);
    if (index > -1) {
      this.polygonClipVolumes.splice(index, 1);
      this.dispatchEvent({
        type: 'polygon_clip_volume_removed',
        scene: this,
        volume: volume,
      });
    }
  }

  addMeasurement(measurement: Measure): void {
    this._measurements.push(measurement);
    this.dispatchEvent({
      type: 'measurement_added',
      scene: this,
      measurement: measurement,
    });
  }

  removeMeasurement(measurement: Measure): void {
    let index = this._measurements.indexOf(measurement);
    if (index > -1) {
      this._measurements.splice(index, 1);
      this.dispatchEvent({
        type: 'measurement_removed',
        scene: this,
        measurement: measurement,
      });
    }
  }

  addProfile(profile: Profile): void {
    this._profiles.push(profile);
    this.dispatchEvent({
      type: 'profile_added',
      scene: this,
      profile: profile,
    });
  }

  removeProfile(profile: Profile): void {
    let index = this._profiles.indexOf(profile);
    if (index > -1) {
      this._profiles.splice(index, 1);
      this.dispatchEvent({
        type: 'profile_removed',
        scene: this,
        profile: profile,
      });
    }
  }

  removeAllMeasurements(): void {
    while (this._measurements.length > 0) {
      this.removeMeasurement(this._measurements[0]);
    }

    while (this._profiles.length > 0) {
      this.removeProfile(this._profiles[0]);
    }

    while (this._volumes.length > 0) {
      this.removeVolume(this._volumes[0]);
    }
  }

  removeAllClipVolumes(): void {
    let clipVolumes = this._volumes.filter(volume => volume.clip === true);
    for (let clipVolume of clipVolumes) {
      this.removeVolume(clipVolume);
    }

    while (this.polygonClipVolumes.length > 0) {
      this.removePolygonClipVolume(this.polygonClipVolumes[0]);
    }
  }

  getActiveCamera(): THREE.Camera | null {
    if (this.overrideCamera) {
      return this.overrideCamera;
    }

    if (this.cameraMode === CameraMode.PERSPECTIVE) {
      return this.cameraP;
    } else if (this.cameraMode === CameraMode.ORTHOGRAPHIC) {
      return this.cameraO;
    }

    return null;
  }

  initialize(): void {
    this.referenceFrame = new THREE.Object3D();
    this.referenceFrame.matrixAutoUpdate = false;
    this.scenePointCloud.add(this.referenceFrame);

    this.cameraP.up.set(0, 0, 1);
    this.cameraP.position.set(1000, 1000, 1000);
    this.cameraO.up.set(0, 0, 1);
    this.cameraO.position.set(1000, 1000, 1000);

    this.cameraScreenSpace.position.set(0, 0, 1);
    this.cameraScreenSpace.up.set(0, 1, 0);
    this.cameraScreenSpace.lookAt(new THREE.Vector3(0, 0, 0));

    this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    this.directionalLight.position.set(10, 10, 10);
    this.directionalLight.lookAt(new THREE.Vector3(0, 0, 0));
    this.scenePointCloud.add(this.directionalLight);

    let light = new THREE.AmbientLight(0x555555);
    this.scenePointCloud.add(light);

    {
      // background
      let texture = Utils.createBackgroundTexture(512, 512);

      texture.minFilter = texture.magFilter = THREE.NearestFilter;
      texture.minFilter = texture.magFilter = THREE.LinearFilter;
      let bg = new THREE.Mesh(
        new THREE.PlaneGeometry(2, 2, 1),
        new THREE.MeshBasicMaterial({
          map: texture,
        }),
      );
      (bg.material as THREE.MeshBasicMaterial).depthTest = false;
      (bg.material as THREE.MeshBasicMaterial).depthWrite = false;
      this.sceneBG.add(bg);
    }
  }

  addAnnotation(
    position: THREE.Vector3 | [number, number, number],
    args: { position?: THREE.Vector3; [key: string]: unknown } = {},
  ): Annotation {
    if (Array.isArray(position)) {
      args.position = new THREE.Vector3().fromArray(position);
    } else if (position.x != null) {
      args.position = position;
    }
    let annotation = new Annotation(args);
    this.annotations.add(annotation);

    return annotation;
  }

  getAnnotations(): Annotation {
    return this.annotations;
  }

  removeAnnotation(annotationToRemove: Annotation): void {
    this.annotations.remove(annotationToRemove);
  }
}
